<template>
  <div class="cs" :class="{ 'cs--open': open }" v-click-outside="close">
    <button type="button" class="cs__control" @click="toggle">
      <span class="cs__value" :class="{ 'cs__value--placeholder': selectedLabel === null }">
        {{ selectedLabel !== null ? selectedLabel : placeholder }}
      </span>
      <span class="cs__arrow">
        <md-icon>expand_more</md-icon>
      </span>
    </button>

    <transition name="cs-drop">
      <ul v-show="open" class="cs__menu" role="listbox">
        <li
            v-for="opt in options"
            :key="String(opt.value)"
            class="cs__option"
            :class="{ 'cs__option--selected': opt.value === value }"
            role="option"
            :aria-selected="opt.value === value ? 'true' : 'false'"
            @click="select(opt.value)"
        >
          <span v-if="tick" class="cs__tick">
            <md-icon v-if="opt.value === value">check</md-icon>
          </span>
          <span class="cs__option-label">{{ opt.label }}</span>
          <button
              v-if="opt.editable"
              type="button"
              class="cs__edit"
              @click.stop="onEdit(opt)"
          >
            <md-icon>edit</md-icon>
          </button>
          <button
              v-if="opt.removable"
              type="button"
              class="cs__remove"
              @click.stop="onRemove(opt)"
          >
            <md-icon>delete_outline</md-icon>
          </button>
        </li>
        <li v-if="actionLabel" class="cs__action" @click="onAction">
          <md-icon>add</md-icon>
          <span>{{ actionLabel }}</span>
        </li>
      </ul>
    </transition>
  </div>
</template>

<script>
/**
 * Lightweight select that looks like a text input (rounded, light fill) with an
 * arrow on the right and a smooth dropdown. v-model driven. Avoids vue-material's
 * md-select entirely so styling is fully under our control.
 *
 * Props:
 *  - value: currently selected value (v-model)
 *  - options: [{ value, label, removable?, editable? }] — removable options get a
 *    delete icon that emits 'remove' (option), editable ones a pencil icon that
 *    emits 'edit' (option); neither selects the option
 *  - placeholder: text shown when nothing is selected
 *  - tick: show a ✓ in front of the selected option inside the open list
 *  - actionLabel: when set, renders a "+ …" action row pinned at the bottom
 *    of the list that emits 'action' (e.g. "Add choice")
 */
export default {
  name: 'CustomSelect',
  props: {
    value: { default: null },
    options: { type: Array, default: () => [] },
    placeholder: { type: String, default: '—' },
    tick: { type: Boolean, default: false },
    actionLabel: { type: String, default: null }
  },
  data() {
    return { open: false };
  },
  computed: {
    selectedLabel() {
      const match = this.options.find(o => o.value === this.value);
      return match ? match.label : null;
    }
  },
  methods: {
    toggle() { this.open = !this.open; },
    close() { this.open = false; },
    select(val) {
      this.$emit('input', val);
      this.open = false;
    },
    onAction() {
      this.open = false;
      this.$emit('action');
    },
    onRemove(opt) {
      this.open = false;
      this.$emit('remove', opt);
    },
    onEdit(opt) {
      this.open = false;
      this.$emit('edit', opt);
    }
  },
  // Small self-contained outside-click directive (no external dependency).
  directives: {
    clickOutside: {
      bind(el, binding) {
        el.__csOutside__ = (e) => { if (!el.contains(e.target)) binding.value(e); };
        document.addEventListener('mousedown', el.__csOutside__);
      },
      unbind(el) {
        document.removeEventListener('mousedown', el.__csOutside__);
        delete el.__csOutside__;
      }
    }
  }
};
</script>

<style scoped>
.cs { position: relative; width: 100%; }

.cs__control {
  display: flex;
  align-items: center;
  width: 100%;
  height: 44px;
  border: 1px solid #dcdcdc;
  border-radius: 8px;
  background: #fafafa;
  padding: 0 0.85rem;
  font-size: 0.9rem;
  color: #202224;
  cursor: pointer;
  box-sizing: border-box;
  text-align: left;
  transition: border-color .15s ease, background .15s ease;
}
.cs__control:hover { border-color: #c2c2c2; }
.cs--open .cs__control { border-color: #999; background: #fff; }

.cs__value { flex: 1; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.cs__value--placeholder { color: #9aa0a6; }

.cs__arrow { display: inline-flex; align-items: center; margin-left: 0.5rem; }
.cs__arrow .md-icon {
  font-size: 20px !important;
  color: #5b626b !important;
  transition: transform .18s ease;
}
.cs--open .cs__arrow .md-icon { transform: rotate(180deg); }

.cs__menu {
  position: absolute;
  z-index: 50;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  margin: 0;
  padding: 4px;
  list-style: none;
  background: #fff;
  border: 1px solid #e3e3e3;
  border-radius: 8px;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.12);
  max-height: 260px;
  overflow-y: auto;
}
.cs__option {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.5rem 0.7rem;
  border-radius: 6px;
  font-size: 0.88rem;
  color: #2c2c2c;
  cursor: pointer;
}
.cs__option:hover { background: #f1f3f5; }
.cs__option--selected { background: #343841; color: #fff; }
.cs__option--selected:hover { background: #343841; }

.cs__tick { width: 20px; flex-shrink: 0; display: inline-flex; align-items: center; }
.cs__tick .md-icon {
  font-size: 16px !important;
  min-width: 16px !important;
  width: 16px !important;
  height: 16px !important;
  color: inherit !important;
}
.cs__option-label { flex: 1; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.cs__remove,
.cs__edit {
  border: none;
  background: transparent;
  cursor: pointer;
  padding: 0;
  margin-left: 0.4rem;
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
}
.cs__remove .md-icon,
.cs__edit .md-icon {
  font-size: 16px !important;
  min-width: 16px !important;
  width: 16px !important;
  height: 16px !important;
  color: #9aa0a6 !important;
}
.cs__remove:hover .md-icon { color: #d9534f !important; }
.cs__edit:hover .md-icon { color: #343841 !important; }
.cs__option--selected .cs__remove .md-icon,
.cs__option--selected .cs__edit .md-icon { color: #cfd3da !important; }
.cs__option--selected .cs__edit:hover .md-icon { color: #fff !important; }

.cs__action {
  display: flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.5rem 0.7rem;
  margin-top: 2px;
  border-top: 1px solid #f1f1f1;
  border-radius: 0 0 6px 6px;
  font-size: 0.88rem;
  font-weight: 600;
  color: #4a5568;
  cursor: pointer;
}
.cs__action:hover { background: #f1f3f5; }
.cs__action .md-icon {
  font-size: 16px !important;
  min-width: 16px !important;
  width: 16px !important;
  height: 16px !important;
  color: inherit !important;
}

/* smooth open/close */
.cs-drop-enter-active, .cs-drop-leave-active { transition: opacity .15s ease, transform .15s ease; }
.cs-drop-enter, .cs-drop-leave-to { opacity: 0; transform: translateY(-6px); }
</style>
